import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authAPI, mediatorAPI } from '../services/api';

// Initial state helper to get data from localStorage immediately and synchronously
const getInitialAuthState = () => {
  const token = localStorage.getItem('token');
  const storedUser = localStorage.getItem('user');
  const storedMediator = localStorage.getItem('mediatorData');
  let user = null;
  let role = null;
  let mediatorId = null;
  let mediatorData = null;

  if (token && storedUser) {
    try {
      user = JSON.parse(storedUser);
      role = user.role?.toLowerCase() || null;

      if (role === 'mediator' && storedMediator) {
        mediatorData = JSON.parse(storedMediator);
        mediatorId = mediatorData.id;
      }
    } catch (e) {
      console.error('AuthContext: Failed to parse initial data', e);
      user = null;
      role = null;
    }
  } else if (token && !storedUser) {
    localStorage.removeItem('token');
    token = null;
  }

  return {
    user,
    token,
    isAuthenticated: !!token && !!user,
    loading: false,
    isInitialized: true,
    role,
    mediatorId,
    mediatorData,
    error: null
  };
};

const initialState = getInitialAuthState();

// Action types
const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  LOAD_USER_START: 'LOAD_USER_START',
  LOAD_USER_SUCCESS: 'LOAD_USER_SUCCESS',
  LOAD_USER_FAILURE: 'LOAD_USER_FAILURE',
  UPDATE_PROFILE: 'UPDATE_PROFILE',
};

// Reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      const loginRole = action.payload.role?.toLowerCase() || action.payload.user?.role?.toLowerCase();
      return {
        ...state,
        loading: false,
        isAuthenticated: true,
        user: { ...action.payload.user, role: loginRole },
        token: action.payload.token,
        role: loginRole,
        error: null,
      };
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        loading: false,
        isAuthenticated: false,
        user: null,
        token: null,
        role: null,
        error: action.payload,
      };
    case 'SET_MEDIATOR_DATA':
      localStorage.setItem('mediatorData', JSON.stringify(action.payload));
      return {
        ...state,
        mediatorId: action.payload.id,
        mediatorData: action.payload
      };
    case AUTH_ACTIONS.LOGOUT:
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('mediatorData');
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        role: null,
        mediatorId: null,
        mediatorData: null,
        loading: false,
      };
    case AUTH_ACTIONS.LOAD_USER_START:
      return {
        ...state,
        loading: true,
      };
    case AUTH_ACTIONS.LOAD_USER_SUCCESS:
      console.log('LOAD_USER_SUCCESS dispatch:', action.payload);
      const loadedRole = action.payload.role?.toLowerCase() || (typeof action.payload === 'string' ? action.payload.toLowerCase() : null);
      console.log('Setting role to:', loadedRole);
      return {
        ...state,
        loading: false,
        isAuthenticated: true,
        user: typeof action.payload === 'object' ? { ...action.payload, role: loadedRole } : action.payload,
        role: loadedRole,
        error: null,
      };
    case AUTH_ACTIONS.LOAD_USER_FAILURE:
      return {
        ...state,
        loading: false,
        isAuthenticated: false,
        user: null,
        token: null,
        role: null,
      };
    case AUTH_ACTIONS.UPDATE_PROFILE:
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };
    case 'SET_MEDIATOR_DATA':
      return {
        ...state,
        mediatorId: action.payload.id,
        mediatorData: action.payload
      };
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Load user from token on mount
  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (token) {
        // If we have stored user, initialize state with it first
        if (storedUser && !state.user) {
          try {
            const parsedUser = JSON.parse(storedUser);
            const userRole = parsedUser.role?.toUpperCase();
            dispatch({
              type: AUTH_ACTIONS.LOAD_USER_SUCCESS,
              payload: { ...parsedUser, role: userRole },
            });

            // If it's a mediator, trigger mediator data fetch immediately
            if (parsedUser.role?.toLowerCase() === 'mediator') {
              fetchMediatorData(parsedUser.email);
            }
          } catch (e) {
            console.error('AuthContext: Failed to parse stored user', e);
          }
        }

        try {
          const response = await authAPI.getProfile();
          
          // Handle the API response format
          let userData;
          const apiData = response.data.data || response.data;
          
          if (apiData) {
            userData = {
              userId: apiData.userId || apiData.id,
              username: apiData.username || apiData.fullName,
              email: apiData.email,
              phoneNumber: apiData.phoneNumber || apiData.mobile,
              role: (apiData.role || state.role)?.toLowerCase() || 'buyer'
            };
            
            // Update localStorage with fresh data
            localStorage.setItem('user', JSON.stringify(userData));
            
            dispatch({
              type: AUTH_ACTIONS.LOAD_USER_SUCCESS,
              payload: userData,
            });

            // If user is a mediator, fetch their mediator-specific data
            if (userData.role === 'MEDIATOR') {
              fetchMediatorData(userData.email);
            }
          }
        } catch (error) {
          console.error('AuthContext: Background profile fetch failed:', error);
          
          // If we already have a user from localStorage (via getInitialAuthState),
          // we don't need to do anything - the app remains authenticated.
          // Only if we have NO user data do we clear everything.
          if (!state.user && !storedUser) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            dispatch({ type: AUTH_ACTIONS.LOAD_USER_FAILURE });
          }
        }
      } else {
        // No token, ensure state is clean (though getInitialAuthState should handle this)
        if (state.isAuthenticated) {
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
      }
    };

    const fetchMediatorData = async (email) => {
      try {
        const medResponse = await mediatorAPI.getMediatorByEmail(email);
        const medData = medResponse.data.data || medResponse.data;
        if (medData) {
          dispatch({ type: 'SET_MEDIATOR_DATA', payload: medData });
        }
      } catch (medError) {
        console.error('AuthContext: Failed to fetch mediator details:', medError);
      }
    };

    loadUser();
  }, []);

  // Login function
  const login = async (credentials) => {
    try {
      dispatch({ type: AUTH_ACTIONS.LOGIN_START });

      const apiCredentials = {
        username: credentials.email,
        password: credentials.password,
        role: credentials.role,
      };

      const response = await authAPI.login(apiCredentials);

      // ✅ Check success FIRST
      if (!response.data.success) {
        const errorMessage = response.data.message || "Login failed";

        dispatch({
          type: AUTH_ACTIONS.LOGIN_FAILURE,
          payload: errorMessage,
        });

        return { success: false, error: errorMessage };
      }

      // ✅ Only access data when success = true
      const data = response.data;
      const loginUser = {
        userId: data.userId,
        username: data.username,
        email: data.email,
        phoneNumber: data.phoneNumber,
        role: data.role?.toUpperCase(),
      };

      const token = data.token;

      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(loginUser));

      dispatch({
        type: AUTH_ACTIONS.LOGIN_SUCCESS,
        payload: { user: loginUser, token, role: loginUser.role },
      });

      console.log("22222userData.role userData.role userData.role userData.role userData.role ", loginUser.role )


      // If user is a mediator, fetch their mediator-specific data immediately
      if (loginUser.role === 'MEDIATOR') {
        try {
          const medResponse = await mediatorAPI.getMediatorByEmail(loginUser.email);
          const medData = medResponse.data.data || medResponse.data;
          if (medData) {
            dispatch({ type: 'SET_MEDIATOR_DATA', payload: medData });
          }
        } catch (medError) {
          console.error('AuthContext: Failed to fetch mediator details after login:', medError);
        }
      }

      return { success: true, user: loginUser };

    } catch (error) {
      const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Something went wrong";

      dispatch({
        type: AUTH_ACTIONS.LOGIN_FAILURE,
        payload: errorMessage,
      });

      return { success: false, error: errorMessage };
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('mediatorData');
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  };

  // Update profile function
  const updateProfile = async (userData) => {
    try {
      const response = await authAPI.updateProfile(userData);
      dispatch({
        type: AUTH_ACTIONS.UPDATE_PROFILE,
        payload: response.data,
      });
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Update failed';
      return { success: false, error: errorMessage };
    }
  };

  // Check if user has specific role
  const hasRole = (role) => {
    if (!state.isAuthenticated || !state.role || !role) return false;
    return state.role.toLowerCase() === role.toLowerCase();
  };

  const getHomeRoute = () => {
    if (hasRole('admin')) return '/admin';
    if (hasRole('mediator')) return '/mediator';
    if (hasRole('seller')) return '/seller';
    if (hasRole('buyer')) return '/buyer';
    return '/';
  };

  const getOrdersRoute = () => {
    if (hasRole('admin')) return '/admin/orders';
    if (hasRole('mediator')) return '/mediator/orders';
    if (hasRole('seller')) return '/seller/orders';
    if (hasRole('buyer')) return '/buyer/orders';
    return getHomeRoute();
  };

  // Check if user has permission
  const hasPermission = (permission) => {
    if (!state.user) return false;
    
    const permissions = {
      admin: ['read', 'write', 'delete', 'manage_users', 'manage_products', 'manage_orders', 'manage_payouts'],
      mediator: ['read', 'write', 'manage_own_orders', 'request_payout'],
      buyer: ['read', 'write', 'manage_own_orders'],
    };

    return permissions[state.role]?.includes(permission) || false;
  };

  const value = {
    ...state,
    login,
    logout,
    updateProfile,
    hasRole,
    hasPermission,
    getHomeRoute,
    getOrdersRoute,
    dispatch,
    mediatorId: state.mediatorId,
    mediatorData: state.mediatorData
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
